import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, MapPin, Cable, Workflow, FlaskConical, Maximize2, Minimize2, Info, Square } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useFullscreen } from "@/lib/useFullscreen";
import { PORTAL, errText, fetchLab, runLab } from "@/lib/aiot";
import { LabScene } from "@/components/aiot/lab/LabScene";
import { useScenarioPlayer } from "@/components/aiot/lab/useScenarioPlayer";
import { PlacePanel } from "@/components/aiot/lab/PlacePanel";
import { WirePanel } from "@/components/aiot/lab/WirePanel";
import { RulePanel } from "@/components/aiot/lab/RulePanel";
import { TestPanel } from "@/components/aiot/lab/TestPanel";
import { CinemaOverlay } from "@/components/aiot/lab/CinemaOverlay";
import { ROOM } from "@/components/aiot/lab/HotelRoom";

const STEPS = [["place", MapPin, "Place", "लगाइए"], ["wire", Cable, "Wire", "वायर"], ["rules", Workflow, "Rules", "रूल"], ["test", FlaskConical, "Test", "टेस्ट"]];
const EMPTY_RULE = { trigger: "", condition: "always", action: "" };

const useDraft = (id, lab) => {
  const [b, setB] = useState({ placements: {}, wiring: {}, rules: [EMPTY_RULE] });
  const ready = useRef(null);
  useEffect(() => {
    ready.current = null;
    if (!lab || lab.id !== id) return;
    const saved = JSON.parse(localStorage.getItem(`aiot-lab-${id}`) || "null") || lab.best?.build;
    setB(saved ? { placements: saved.placements || {}, wiring: saved.wiring || {}, rules: saved.rules?.length ? saved.rules : [EMPTY_RULE] } : { placements: {}, wiring: {}, rules: [EMPTY_RULE] });
    ready.current = id;
  }, [id, lab]);
  useEffect(() => { if (ready.current === id) localStorage.setItem(`aiot-lab-${id}`, JSON.stringify(b)); }, [id, b]);
  return [b, setB];
};

export default function AiotLab() {
  const { lab: id } = useParams();
  const { L, lang } = useLang();
  const [lab, setLab] = useState(null);
  const [err, setErr] = useState("");
  const [step, setStep] = useState("place");
  const [sel, setSel] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runErr, setRunErr] = useState("");
  const [cinema, setCinema] = useState(false);
  const [b, setB] = useDraft(id, lab);
  const box = useRef(null);
  const { full, toggle } = useFullscreen(box);
  const player = useScenarioPlayer({ id: ROOM[id] ? id : "home" });
  const snapRef = useRef(null);
  const snap = () => snapRef.current?.();

  useEffect(() => { setLab(null); setRes(null); fetchLab(id).then(setLab).catch((e) => setErr(errText(e, "Could not load the lab."))); }, [id]);
  useEffect(() => { if (lab) document.title = `${lab.title} | RoboSpark AIoT`; }, [lab]);

  const pairs = (r) => r.scenarios.map((x) => [lab.scenarios.find((s) => s.id === x.id), x]);
  const run = async () => {
    setBusy(true); setRunErr("");
    try {
      const r = await runLab(id, { placements: b.placements, wiring: b.wiring, rules: b.rules.filter((x) => x.trigger && x.action) });
      setRes(r);
      player.play(pairs(r));
    } catch (e) { setRunErr(errText(e, L("Could not run the tests.", "टेस्ट नहीं चल पाए।"))); } finally { setBusy(false); }
  };
  const showcase = () => { setCinema(true); player.play(pairs(res), { intro: true, outro: true }); };
  const exitCinema = () => { player.stop(); setCinema(false); };
  const onSpot = (k) => { if (sel) { setB({ ...b, placements: { ...b.placements, [sel]: k } }); setSel(null); } };

  if (err) return <div className="min-h-screen bg-[#F3F8FB] grid place-items-center text-sm font-semibold text-[#991B1B]" data-testid="lab-error">{err}</div>;
  if (!lab) return <div className="min-h-screen bg-[#F3F8FB] grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#0F766E]" /></div>;

  const cert = res?.cert || lab.cert;
  const panel = {
    place: <PlacePanel lab={lab} placements={b.placements} setPlacements={(p) => setB({ ...b, placements: p })} sel={sel} setSel={setSel} why={res?.why} />,
    wire: <WirePanel lab={lab} wiring={b.wiring} setWiring={(w) => setB({ ...b, wiring: w })} bad={res?.wire} />,
    rules: <RulePanel lab={lab} rules={b.rules} setRules={(r) => setB({ ...b, rules: r })} />,
    test: <TestPanel lab={lab} res={res} busy={busy} onRun={run} onReplay={(r) => player.play([[lab.scenarios.find((s) => s.id === r.id), r]])} onShowcase={showcase} err={runErr} cert={cert} snap={snap} />,
  }[step];

  return (
    <div className="bg-[#F3F8FB] min-h-screen" data-testid="aiot-lab-page">
      <div className="max-w-[1500px] mx-auto px-4 lg:px-6 py-5">
        <div className="flex flex-wrap items-center gap-3">
          <Link to={`${PORTAL}?view=labs`} className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-[#0F766E]" data-testid="lab-back"><ArrowLeft className="w-4 h-4" />{L("Course portal", "कोर्स पोर्टल")}</Link>
          <span className="text-slate-300">/</span>
          <h1 className="text-lg sm:text-xl font-extrabold text-[#0B1F3A]" data-testid="lab-title">{lang === "hi" ? lab.title_hi : lab.title}</h1>
          {lab.best && <span className="rounded-full bg-[#ECFDF5] text-[#059669] text-xs font-black px-2.5 py-1" data-testid="lab-best">{L("Best", "सबसे अच्छा")} {lab.best.score}/100</span>}
        </div>
        <p className="mt-1 text-sm text-slate-600 max-w-4xl">{lang === "hi" ? lab.brief_hi : lab.brief}</p>
        {!lab.enrolled && <p className="mt-2 text-xs font-semibold text-[#B45309] inline-flex items-center gap-1.5" data-testid="lab-practice-note"><Info className="w-3.5 h-3.5" />{L("Practice mode. Scores are saved for enrolled diploma students.", "प्रैक्टिस मोड। स्कोर सिर्फ़ एनरोल्ड डिप्लोमा छात्रों के लिए सेव होते हैं।")}</p>}
        <div className="mt-4 grid lg:grid-cols-[1fr_430px] gap-5 items-start">
          <div ref={box} className={`relative overflow-hidden bg-[#EAF2FB] ${full ? "fixed inset-0 z-[100]" : "rounded-3xl border border-slate-200 h-[52vh] lg:h-[calc(100vh-190px)] min-h-[420px]"}`}>
            <LabScene lab={lab} placements={b.placements} selected={sel} onSpot={onSpot} showSpots={step === "place" && !cinema} fx={player.fx} cam={player.cam} cinematic={cinema} lang={lang} snapRef={snapRef} />
            {cinema && res && <CinemaOverlay lab={lab} res={res} cap={player.cap} playing={player.playing} onReplay={showcase} onExit={exitCinema} full={full} toggle={toggle} cert={cert} snap={snap} />}
            {!cinema && (
              <>
                <button type="button" onClick={toggle} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur grid place-items-center text-[#0B1F3A] shadow" aria-label="Full screen" data-testid="lab-fullscreen">{full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}</button>
                {sel && <div className="absolute left-3 top-3 rounded-full bg-[#0F766E] text-white text-xs font-bold px-3 py-1.5 shadow" data-testid="lab-placing">{L("Tap a blue point to place", "लगाने के लिए नीला पॉइंट tap करें")}: {lang === "hi" ? lab.devices[sel].hi : lab.devices[sel].en}</div>}
                {player.cap && player.cap.s && (
                  <div className="absolute left-3 bottom-3 right-3 rounded-2xl bg-white/90 backdrop-blur pl-4 pr-2 py-2 text-sm font-bold text-[#0B1F3A] shadow flex items-center gap-2" data-testid="lab-playing">
                    <span className="flex-1 min-w-0">{lang === "hi" ? player.cap.s.hi : player.cap.s.en}{player.cap.phase === 1 && player.cap.r && <span className={`ml-2 ${player.cap.r.pass ? "text-[#059669]" : "text-[#DC2626]"}`}>{player.cap.r.pass ? "✓" : "✗"}</span>}</span>
                    <button type="button" onClick={player.stop} className="shrink-0 rounded-full bg-[#0B1F3A] text-white px-3 h-8 text-xs font-bold inline-flex items-center gap-1 hover:bg-[#0F766E]" data-testid="lab-stop-playback"><Square className="w-3 h-3" />{L("Skip", "रोकें")}</button>
                  </div>
                )}
              </>
            )}
          </div>
          <aside className="rounded-3xl border border-slate-200 bg-white p-4 lg:max-h-[calc(100vh-190px)] lg:overflow-y-auto" data-testid="lab-panel">
            <div className="grid grid-cols-4 gap-1 rounded-2xl bg-slate-100 p-1 mb-4">
              {STEPS.map(([k, I, en, hi], i) => (
                <button key={k} type="button" onClick={() => setStep(k)} className={`rounded-xl py-2 text-xs font-bold inline-flex flex-col items-center gap-0.5 transition-colors ${step === k ? "bg-white text-[#0F766E] shadow" : "text-slate-500 hover:text-[#0B1F3A]"}`} data-testid={`lab-step-${k}`}>
                  <I className="w-4 h-4" />{i + 1}. {L(en, hi)}
                </button>
              ))}
            </div>
            {panel}
          </aside>
        </div>
      </div>
    </div>
  );
}
